import { inject, Injectable } from '@angular/core';
import { DatabaseModel } from '../../models/class/database-model.class';
import { Table } from '../../models/class/table.class';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { DatabaseModelBuilder } from '../../models/builder/database-model-builder.class';
import { SavedDatabaseModel } from '../../models/saved/saved-database-model.type';
import { Link } from '../../models/class/link.class';
import { NULL_LENGTH, UNKNOWN_INDEX } from '../../models/constant/constants.constant';
import { LocalStorageService } from '../../../../core/services/local-storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class DatabaseModelService {
  private _localStorageService = inject(LocalStorageService);
  private _model$: BehaviorSubject<DatabaseModel> = new BehaviorSubject<DatabaseModel>(new DatabaseModel('', [], []));

  public getModel$(): Observable<DatabaseModel> {
    if (this._model$.value.getTitle() === '') {
      this._model$.next(this._getStoredModel());
    }
    return this._model$.asObservable();
  }

  public getActualModel$(): Observable<SavedDatabaseModel> {
    this.getModel$();
    return this._model$.pipe(
      map((model: DatabaseModel) => (model.getTitle().length === NULL_LENGTH ? this._getStoredModel() : model)),
      map(model => DatabaseModel.mapToSaved(model))
    );
  }

  public setModel(model: DatabaseModel): void {
    this._model$.next(DatabaseModelBuilder.buildFromClass(model));
    this._saveStoredModel();
  }

  public addTable(table: Table): void {
    this._model$.next(DatabaseModelBuilder.buildFromClass(this._model$.value.addTable(table)));
    this._saveStoredModel();
  }

  public updateTitle(newTitle: string): void {
    this._model$.next(DatabaseModelBuilder.buildFromClass(this._model$.value.setTitle(newTitle)));
    this._saveStoredModel();
  }

  public tableExists$(tableName: string): Observable<boolean> {
    return this._model$.pipe(
      map(
        (model: DatabaseModel) =>
          model
            .getTableList()
            .map((table: Table) => table.getTitle())
            .indexOf(tableName) != UNKNOWN_INDEX
      )
    );
  }

  public getTablesNames$(): Observable<string[]> {
    return this._model$.pipe(
      map((model: DatabaseModel) => model.getTableList()),
      map((tableList: Table[]) => tableList.map((table: Table) => table.getTitle()))
    );
  }

  public addLink$(link: Link): void {
    this._model$.next(DatabaseModelBuilder.buildFromClass(this._model$.value.addLink(link)));
    this._saveStoredModel();
  }

  private _getStoredModel(): DatabaseModel {
    try {
      return this._localStorageService.loadModel();
      // eslint-disable-next-line
    } catch (e) {
      return new DatabaseModel('', [], []);
    }
  }

  private _saveStoredModel(): void {
    this._localStorageService.saveModel(this._model$.value);
  }

  public linkExist$(table1: string, table2: string): Observable<boolean> {
    return this._model$.pipe(
      map((model: DatabaseModel) => model.getLinkList()),
      map((links: Link[]) => this._linkExists(links, table1, table2))
    );
  }

  private _linkExists(links: Link[], table1: string, table2: string): boolean {
    for (const link of links) {
      if ((link.table1 === table1 && link.table2 === table2) || (link.table2 === table1 && link.table1 === table2)) {
        return true;
      }
    }
    return false;
  }

  public deleteLink$(link: Link): void {
    this._model$.next(DatabaseModelBuilder.buildFromClass(this._model$.value.deleteLink(link)));
    this._saveStoredModel();
  }

  public deleteTable(table: Table): void {
    this._model$.next(DatabaseModelBuilder.buildFromClass(this._model$.value.deleteTable(table)));
    this._saveStoredModel();
  }
}
